import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { graphql as commonGraphql } from '@sitebuilder/common';
import { ThunkResult } from './index';

export interface StateShape {
    [key: string]: {
        graphqlQueryParams: coreTypes.GraphQLQueryParams | null;
        isQueryLoading: boolean;
        isQueryDataLoaded: boolean;
        queryDataLoadedError: boolean;
        graphQLQueryResult: coreTypes.GraphQLQueryResult | null;
    };
}

export const queryDefaultState: StateShape = {};

const queryApiClient = new commonGraphql.QueryApiClient();

export const actionCreators = {
    loadQueryData: (
        id: string,
        graphqlQueryParams: coreTypes.GraphQLQueryParams
    ): ThunkResult<Promise<coreTypes.GraphQLQueryResult | null>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const exists = state.query[id];

            if (exists) {
                return exists.graphQLQueryResult;
            }

            dispatch(querySliceReducerActions.QUERY_LOAD_START({ id, graphqlQueryParams }));

            try {
                const graphQLQueryResult = await queryApiClient.executeQuery(graphqlQueryParams);

                if (graphQLQueryResult) {
                    dispatch(querySliceReducerActions.QUERY_LOAD_END({ id, graphQLQueryResult }));
                }

                return graphQLQueryResult;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    querySliceReducerActions.QUERY_LOAD_END({ id, error: { message: err.message } })
                );
                return null;
            }
        };
    },
    downloadWithQuery: (
        id: string,
        graphqlQueryParams: coreTypes.DownloadWithGraphQLQueryParams
    ): ThunkResult<Promise<coreTypes.GraphQLQueryResult | null>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const exists = state.query[id];

            if (exists) {
                return exists.graphQLQueryResult;
            }

            dispatch(querySliceReducerActions.QUERY_LOAD_START({ id, graphqlQueryParams }));

            try {
                const graphQLQueryResult =
                    await queryApiClient.downloadWithQuery(graphqlQueryParams);

                if (graphQLQueryResult) {
                    dispatch(querySliceReducerActions.QUERY_LOAD_END({ id, graphQLQueryResult }));
                }

                return graphQLQueryResult;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    querySliceReducerActions.QUERY_LOAD_END({ id, error: { message: err.message } })
                );
                return null;
            }
        };
    },
};

const querySlice = createSlice({
    name: 'query',
    initialState: queryDefaultState,
    reducers: {
        QUERY_LOAD_START(
            draft,
            action: PayloadAction<{
                id: string;
                graphqlQueryParams?: coreTypes.GraphQLQueryParams;
            }>
        ) {
            const { id, graphqlQueryParams } = action.payload;
            draft[id] = {
                isQueryLoading: true,
                isQueryDataLoaded: false,
                queryDataLoadedError: false,
                graphqlQueryParams: graphqlQueryParams || null,
                graphQLQueryResult: null,
            };
        },
        QUERY_LOAD_END(
            draft,
            action: PayloadAction<{
                id: string;
                graphQLQueryResult?: coreTypes.GraphQLQueryResult;
                error?: any;
            }>
        ) {
            const { id, graphQLQueryResult, error } = action.payload;
            const item = draft[id];
            if (item) {
                item.isQueryLoading = false;
                item.isQueryDataLoaded = true;
                item.graphQLQueryResult = graphQLQueryResult || null;
                item.queryDataLoadedError = error || null;
            }
        },
    },
});

export const querySliceReducerActions: any = querySlice.actions;
export const queryReducer = querySlice.reducer;
